import React, { useEffect, useMemo, useState } from 'react';
import { Copy } from 'lucide-react';
import Sheet from '../common/Sheet';
import { useToast } from '../common/Toast';
import WarningList from './WarningList';
import { ClinicianPicker, PatientPicker, PickerOption, clinicianOption } from './Pickers';
import { ScheduleService, scheduleErrorMessage, warningsFromError } from '../../services/scheduling';
import { useDebounce } from '../../hooks/useDebounce';
import type { UserData } from '../../dtos/admin/user.dto';
import {
  DISCIPLINES, Discipline, DryRunResult, EditScope, SHIFT_VISIT_TYPES, ScheduleWarning, Shift, ShiftVisitType, ShiftWriteResult,
} from '../../dtos/scheduling/schedule.dto';
import {
  RECURRENCE_PRESETS, ShiftFormState, TIME_PRESETS, addDays, dayLabel, emptyShiftForm, formatMinutes, hhaxCopyText,
  missingFields, needsConfirmation, toCreateRequest, weekdayLabel, windowMinutes,
} from './scheduleUtils';

export interface ShiftDrawerProps {
  open: boolean;
  onClose: () => void;
  onSaved: (result: ShiftWriteResult) => void;
  /** Edit an existing shift; otherwise create */
  shift?: Shift | null;
  /** Prefill for create (clicked cell, current patient, last clinician) */
  prefill?: Partial<ShiftFormState> & { patientLabel?: string; providerLabel?: string };
  /** Lock the patient (the patient chart's Schedule tab) */
  lockPatient?: boolean;
  clinicians: UserData[];
  today: string;
  onCancelShift?: (shift: Shift) => void;
}

const DAY_CHIPS = [1, 2, 3, 4, 5, 6, 0];
const chip = (active: boolean) => `min-h-[44px] rounded-full border px-3 text-sm font-medium md:min-h-[36px] ${active ? 'border-slate-800 bg-slate-800 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'}`;
const inputCls = 'min-h-[44px] w-full rounded-md border border-gray-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-500 md:text-sm';

/**
 * Create or edit a shift in a side panel (plan §6.1). Prefilled from context, one-tap time and
 * repeat presets, end time from the window, and a live dry run that shows the shift count,
 * hours, PA units and warnings before anything is saved.
 */
const ShiftDrawer: React.FC<ShiftDrawerProps> = ({ open, onClose, onSaved, shift, prefill, lockPatient, clinicians, today, onCancelShift }) => {
  const { showToast } = useToast();
  const editing = Boolean(shift);
  const [form, setForm] = useState<ShiftFormState>(() => emptyShiftForm(today));
  const [patient, setPatient] = useState<PickerOption | null>(null);
  const [scope, setScope] = useState<EditScope>('this');
  const [check, setCheck] = useState<DryRunResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [addToCareTeam, setAddToCareTeam] = useState(true);

  // Reset whenever the drawer opens
  useEffect(() => {
    if (!open) return;
    setError(null);
    setCheck(null);
    setScope('this');
    setAddToCareTeam(true);
    if (shift) {
      setForm(emptyShiftForm(shift.date, {
        patientId: shift.patientId, providerId: shift.providerId, discipline: (shift.discipline as Discipline) || '',
        visitType: shift.visitType, startTime: shift.startTime, endTime: shift.endTime, notes: shift.notes || '', authId: shift.authId,
      }));
      setPatient({ id: shift.patientId, label: shift.patient?.name || 'Patient' });
    } else {
      const base = emptyShiftForm(prefill?.date || today, prefill);
      setForm(base);
      setPatient(prefill?.patientId ? { id: prefill.patientId, label: prefill.patientLabel || 'Patient' } : null);
    }
  }, [open, shift, prefill, today]);

  const set = <K extends keyof ShiftFormState>(key: K, value: ShiftFormState[K]) => setForm((f) => ({ ...f, [key]: value }));
  const providerValue = useMemo(() => {
    if (!form.providerId) return null;
    const u = clinicians.find((c) => c.id === form.providerId);
    return u ? clinicianOption(u, today) : { id: form.providerId, label: shift?.provider?.name || prefill?.providerLabel || 'Clinician' };
  }, [form.providerId, clinicians, today, shift, prefill]);
  const missing = missingFields(form);
  const minutes = windowMinutes(form.startTime, form.endTime);

  // Live dry run (debounced) once the required fields are filled
  const debouncedForm = useDebounce(form, 500);
  useEffect(() => {
    if (!open || missingFields(debouncedForm).length) { setCheck(null); return undefined; }
    let live = true;
    setChecking(true);
    const run = editing && shift
      ? ScheduleService.previewUpdate(shift.id, {
        providerId: debouncedForm.providerId, discipline: debouncedForm.discipline || undefined, visitType: debouncedForm.visitType,
        startTime: debouncedForm.startTime, endTime: debouncedForm.endTime, notes: debouncedForm.notes,
        ...(scope === 'this' ? { date: debouncedForm.date } : {}),
      }, scope)
      : ScheduleService.preview(toCreateRequest(debouncedForm));
    run.then((r) => { if (live) { setCheck(r); setError(null); } })
      .catch((e) => { if (live) { setCheck(null); setError(scheduleErrorMessage(e, 'Could not check this shift')); } })
      .finally(() => live && setChecking(false));
    return () => { live = false; };
  }, [debouncedForm, open, editing, shift, scope]);

  const warnings: ScheduleWarning[] = check?.warnings || [];
  const confirm = needsConfirmation(warnings);

  const save = async () => {
    if (missing.length) return;
    setSaving(true);
    setError(null);
    try {
      const extra = { acknowledgeWarnings: confirm, addToCareTeam };
      const result = editing && shift
        ? await ScheduleService.update(shift.id, {
          providerId: form.providerId, discipline: form.discipline || undefined, visitType: form.visitType,
          startTime: form.startTime, endTime: form.endTime, notes: form.notes, ...(scope === 'this' ? { date: form.date } : {}), ...extra,
        }, scope)
        : await ScheduleService.create(toCreateRequest(form, extra));
      const n = result.shifts.length;
      showToast(editing ? `Updated ${n} shift${n === 1 ? '' : 's'}` : `Scheduled ${n} shift${n === 1 ? '' : 's'}`, { variant: 'success' });
      onSaved(result);
    } catch (e) {
      const w = warningsFromError(e);
      if (w) setCheck((c) => (c ? { ...c, warnings: w } : { dryRun: true, warnings: w, preview: { count: 0, firstDate: null, lastDate: null, dates: [], totalMinutes: 0, hours: 0, paUnits: [] } }));
      else setError(scheduleErrorMessage(e, 'Could not save the shift'));
    } finally {
      setSaving(false);
    }
  };

  const preview = check?.preview;
  const title = editing ? 'Edit shift' : 'New shift';
  const subtitle = editing && shift ? `${dayLabel(shift.date)} · ${shift.scheduleRef || ''}` : patient?.label;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      footer={(
        <div className="flex flex-wrap items-center gap-2">
          {editing && shift && onCancelShift && shift.status === 'SCHEDULED' && (
            <button type="button" className="min-h-[44px] rounded-md px-3 text-sm font-medium text-red-700 hover:bg-red-50" onClick={() => onCancelShift(shift)}>
              Cancel shift…
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button type="button" className="min-h-[44px] rounded-md border border-gray-300 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50" onClick={onClose}>Close</button>
            <button
              type="button"
              disabled={saving || missing.length > 0 || (editing && shift?.status !== 'SCHEDULED')}
              onClick={save}
              className={`min-h-[44px] rounded-md px-4 text-sm font-semibold text-white disabled:opacity-50 ${confirm ? 'bg-amber-600 hover:bg-amber-700' : 'bg-slate-800 hover:bg-slate-900'}`}
            >
              {saving ? 'Saving…' : confirm ? (editing ? 'Save anyway' : 'Schedule anyway') : (editing ? 'Save' : 'Schedule')}
            </button>
          </div>
        </div>
      )}
    >
      <div className="space-y-5">
        {editing && shift?.isRecurring && (
          <fieldset>
            <legend className="mb-1 text-sm font-medium text-gray-700">Apply changes to</legend>
            <div className="flex flex-wrap gap-2" role="radiogroup">
              {([['this', 'This shift'], ['following', 'This and following'], ['all', 'All future']] as [EditScope, string][]).map(([value, label]) => (
                <button key={value} type="button" role="radio" aria-checked={scope === value} className={chip(scope === value)} onClick={() => setScope(value)}>{label}</button>
              ))}
            </div>
          </fieldset>
        )}

        <PatientPicker
          value={patient}
          disabled={lockPatient || editing}
          onChange={(o) => { setPatient(o); set('patientId', o?.id || ''); }}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <ClinicianPicker
            value={providerValue}
            clinicians={clinicians}
            today={today}
            onChange={(o) => set('providerId', o?.id || null)}
          />
          <div>
            <label htmlFor="shift-discipline" className="mb-1 block text-sm font-medium text-gray-700">
              Discipline{!form.providerId && <span className="text-red-600"> *</span>}
            </label>
            <select id="shift-discipline" className={inputCls} value={form.discipline} onChange={(e) => set('discipline', e.target.value as Discipline | '')}>
              <option value="">{form.providerId ? 'From the clinician' : 'Choose…'}</option>
              {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>

        <fieldset>
          <legend className="mb-1 text-sm font-medium text-gray-700">Visit type</legend>
          <div className="flex flex-wrap gap-2">
            {SHIFT_VISIT_TYPES.map((v) => (
              <button key={v} type="button" className={chip(form.visitType === v)} onClick={() => set('visitType', v as ShiftVisitType)}>{v}</button>
            ))}
          </div>
        </fieldset>

        <div>
          <label htmlFor="shift-date" className="mb-1 block text-sm font-medium text-gray-700">{form.recurrence === 'none' ? 'Date' : 'First date'}</label>
          <input
            id="shift-date"
            type="date"
            className={inputCls}
            value={form.date}
            disabled={editing && scope !== 'this'}
            onChange={(e) => {
              const date = e.target.value;
              setForm((f) => ({ ...f, date, customDays: f.recurrence === 'custom' ? f.customDays : [new Date(`${date}T00:00:00Z`).getUTCDay()] }));
            }}
          />
        </div>

        <fieldset>
          <legend className="mb-1 text-sm font-medium text-gray-700">Time</legend>
          <div className="mb-2 flex flex-wrap gap-2">
            {TIME_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={chip(form.startTime === p.startTime && form.endTime === p.endTime)}
                onClick={() => setForm((f) => ({ ...f, startTime: p.startTime, endTime: p.endTime }))}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input aria-label="Start time" type="time" className={inputCls} value={form.startTime} onChange={(e) => set('startTime', e.target.value)} />
            <span className="text-gray-400">–</span>
            <input aria-label="End time" type="time" className={inputCls} value={form.endTime} onChange={(e) => set('endTime', e.target.value)} />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {formatMinutes(minutes)}{form.endTime <= form.startTime ? ' · ends the next day' : ''}
          </p>
        </fieldset>

        {!editing && (
          <fieldset>
            <legend className="mb-1 text-sm font-medium text-gray-700">Repeat</legend>
            <div className="flex flex-wrap gap-2">
              {RECURRENCE_PRESETS.map((p) => (
                <button key={p.id} type="button" className={chip(form.recurrence === p.id)} onClick={() => set('recurrence', p.id)}>{p.label}</button>
              ))}
            </div>
            {form.recurrence === 'custom' && (
              <div className="mt-3 space-y-3">
                <div className="flex flex-wrap gap-1.5">
                  {DAY_CHIPS.map((d) => {
                    const on = form.customDays.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        aria-pressed={on}
                        className={`h-11 w-11 rounded-full border text-sm font-medium ${on ? 'border-slate-800 bg-slate-800 text-white' : 'border-gray-300 text-gray-700'}`}
                        onClick={() => set('customDays', on ? form.customDays.filter((x) => x !== d) : [...form.customDays, d])}
                      >
                        {weekdayLabel(addDays('2026-10-11', d)).slice(0, 2)}
                      </button>
                    );
                  })}
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  Every
                  <select className="min-h-[44px] rounded-md border border-gray-300 px-2" value={form.interval} onChange={(e) => set('interval', Number(e.target.value))}>
                    {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                  week{form.interval > 1 ? 's' : ''}
                </label>
              </div>
            )}
            {form.recurrence !== 'none' && (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-gray-700">
                <span>Ends</span>
                <select
                  className="min-h-[44px] rounded-md border border-gray-300 px-2"
                  value={form.end.kind}
                  onChange={(e) => set('end', e.target.value === 'count' ? { kind: 'count', count: 12 } : { kind: 'until', until: addDays(form.date, 27) })}
                >
                  <option value="until">on</option>
                  <option value="count">after</option>
                </select>
                {form.end.kind === 'until' ? (
                  <input type="date" aria-label="End date" className="min-h-[44px] rounded-md border border-gray-300 px-2" value={form.end.until} min={form.date} onChange={(e) => set('end', { kind: 'until', until: e.target.value })} />
                ) : (
                  <>
                    <input type="number" inputMode="numeric" min={1} max={500} aria-label="Number of shifts" className="min-h-[44px] w-20 rounded-md border border-gray-300 px-2" value={form.end.count} onChange={(e) => set('end', { kind: 'count', count: Math.max(1, Number(e.target.value) || 1) })} />
                    <span>shifts</span>
                  </>
                )}
              </div>
            )}
          </fieldset>
        )}

        <div>
          <label htmlFor="shift-notes" className="mb-1 block text-sm font-medium text-gray-700">Notes for the clinician</label>
          <textarea id="shift-notes" rows={2} maxLength={1000} className={`${inputCls} py-2`} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </div>

        <section aria-label="Check" className="space-y-3">
          {missing.length > 0 && <p className="text-sm text-gray-500">Still needed: {missing.join(', ')}.</p>}
          {checking && <p className="text-sm text-gray-500">Checking…</p>}
          {preview && preview.count > 0 && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
              <span className="font-medium">{preview.count} shift{preview.count === 1 ? '' : 's'}</span>
              {' · '}{preview.hours} h
              {preview.paUnits.map((u) => (
                <span key={u.authorizationId}> · uses {u.units} of {u.approved - u.used} {u.unitType} left on the PA</span>
              ))}
              {preview.count > 1 && preview.lastDate && <div className="mt-0.5 text-xs text-gray-500">{dayLabel(preview.firstDate || form.date)} to {dayLabel(preview.lastDate)}; later shifts are added automatically each night.</div>}
              {check?.targetCount !== undefined && <div className="mt-0.5 text-xs text-gray-500">Changes {check.targetCount} shift{check.targetCount === 1 ? '' : 's'}.</div>}
            </div>
          )}
          <WarningList warnings={warnings} addToCareTeam={addToCareTeam} onAddToCareTeamChange={setAddToCareTeam} />
          {error && <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        </section>

        {editing && shift?.scheduleRef && (
          <button
            type="button"
            className="flex min-h-[44px] items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            onClick={async () => {
              try { await navigator.clipboard.writeText(hhaxCopyText(shift)); showToast('Copied for HHAX', { variant: 'success', durationMs: 2500 }); } catch { /* ignore */ }
            }}
          >
            <Copy size={16} /> Copy for HHAX ({shift.scheduleRef}{shift.provider?.clinicianCode ? ` · ${shift.provider.clinicianCode}` : ''})
          </button>
        )}
      </div>
    </Sheet>
  );
};

export default ShiftDrawer;
